import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Head, router, useForm } from '@inertiajs/react'
import { Plus, Search, Download, Filter, ChevronDown, Check, CalendarDays } from 'lucide-react'
import DashboardLayout from '@/components/DashboardLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TagCombobox } from '@/components/TagCombobox'
import type { GuestEventOption, GuestFilters, GuestRow, GuestSortKey, PaginationMeta } from '@/types'

type Props = {
  guests: GuestRow[]
  pagination: PaginationMeta
  available_tags: string[]
  available_events: GuestEventOption[]
  filter_events: GuestEventOption[]
  filters: GuestFilters
}

const SORT_OPTIONS: { value: GuestSortKey; label: string }[] = [
  { value: 'last_purchase_desc', label: 'Last purchase (newest)' },
  { value: 'total_spent_desc', label: 'Total spent (highest)' },
  { value: 'name_asc', label: 'Name (A → Z)' },
  { value: 'recent_desc', label: 'Recently added' },
]

function dollars(cents: number) {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatDate(value: string | null) {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

export default function DashboardGuestsIndex({ guests, pagination, available_tags, available_events, filter_events, filters }: Props) {
  const [accumulated, setAccumulated] = useState<GuestRow[]>(guests)
  const [searchValue, setSearchValue] = useState(filters.q)
  const debounceRef = useRef<number | null>(null)

  // Reset list whenever the index page re-renders for page=1 (filter/sort change).
  useEffect(() => {
    if (pagination.page === 1) setAccumulated(guests)
    else setAccumulated(prev => mergeGuests(prev, guests))
  }, [guests, pagination.page])

  // Debounced search
  useEffect(() => {
    if (searchValue === filters.q) return
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => {
      applyFilters({ q: searchValue, tags: filters.tags, event_id: filters.event_id, sort: filters.sort, page: 1 })
    }, 250)
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current) }
  }, [searchValue]) // eslint-disable-line react-hooks/exhaustive-deps

  function applyFilters(next: { q: string; tags: string[]; event_id: string; sort: GuestSortKey; page: number }) {
    const data: Record<string, string | string[]> = { sort: next.sort }
    if (next.q) data.q = next.q
    if (next.tags.length > 0) data.tags = next.tags
    if (next.event_id) data.event_id = next.event_id
    if (next.page > 1) data.page = String(next.page)
    router.get('/dashboard/guests', data, { preserveScroll: true, preserveState: true, replace: true })
  }

  function toggleTag(tag: string) {
    const next = filters.tags.includes(tag)
      ? filters.tags.filter(t => t !== tag)
      : [...filters.tags, tag]
    applyFilters({ q: filters.q, tags: next, event_id: filters.event_id, sort: filters.sort, page: 1 })
  }

  function changeEvent(eventId: string) {
    applyFilters({ q: filters.q, tags: filters.tags, event_id: eventId, sort: filters.sort, page: 1 })
  }

  function changeSort(value: string) {
    applyFilters({ q: filters.q, tags: filters.tags, event_id: filters.event_id, sort: value as GuestSortKey, page: 1 })
  }

  function loadMore() {
    if (!pagination.next) return
    router.reload({
      only: ['guests', 'pagination'],
      data: {
        ...(filters.q ? { q: filters.q } : {}),
        ...(filters.tags.length > 0 ? { tags: filters.tags } : {}),
        ...(filters.event_id ? { event_id: filters.event_id } : {}),
        sort: filters.sort,
        page: pagination.next,
      },
    })
  }

  const exportHref = useMemo(() => {
    const params = new URLSearchParams()
    if (filters.q) params.set('q', filters.q)
    filters.tags.forEach(t => params.append('tags[]', t))
    if (filters.event_id) params.set('event_id', filters.event_id)
    if (filters.sort) params.set('sort', filters.sort)
    return `/dashboard/guests/export_csv${params.toString() ? `?${params.toString()}` : ''}`
  }, [filters])

  return (
    <>
      <Head title="Transactions" />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Transactions</h1>
            <p className="mt-1 text-base text-muted-foreground">
              Everyone who&rsquo;s bought from you, plus anyone you&rsquo;ve added by hand.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <a href={exportHref}>
                <Download className="size-4" />
                Export CSV
              </a>
            </Button>
            <AddGuestDialog availableEvents={available_events} availableTags={available_tags} />
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              placeholder="Search name or email…"
              className="pl-9"
              aria-label="Search guests"
            />
          </div>

          <TagFilterPopover
            availableTags={available_tags}
            selected={filters.tags}
            onToggle={toggleTag}
            onClear={() => applyFilters({ q: filters.q, tags: [], event_id: filters.event_id, sort: filters.sort, page: 1 })}
          />

          <EventFilterPopover
            availableEvents={filter_events}
            selected={filters.event_id}
            onChange={changeEvent}
          />

          <Select value={filters.sort} onValueChange={changeSort}>
            <SelectTrigger className="w-[230px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="popper" align="end">
              <SelectGroup>
                {SORT_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {accumulated.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-black/10 bg-muted/20 p-12 text-center">
            <p className="text-base text-muted-foreground">
              {filters.q || filters.tags.length > 0 || filters.event_id
                ? 'No guests match your filters.'
                : 'No guests yet. Once buyers complete checkout, they show up here.'}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead className="text-right">Purchases</TableHead>
                  <TableHead className="text-right">Total spent</TableHead>
                  <TableHead>Last purchase</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accumulated.map(guest => (
                  <TableRow
                    key={guest.id}
                    className="cursor-pointer"
                    onClick={() => router.visit(`/dashboard/guests/${guest.id}`)}
                  >
                    <TableCell className="font-medium">{guest.display_name}</TableCell>
                    <TableCell className="text-muted-foreground">{guest.email}</TableCell>
                    <TableCell>
                      {guest.tags.length === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {guest.tags.slice(0, 3).map(tag => (
                            <Badge key={tag} variant="secondary" className="text-[11px]">{tag}</Badge>
                          ))}
                          {guest.tags.length > 3 && (
                            <span className="text-xs text-muted-foreground">+{guest.tags.length - 3}</span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{guest.purchase_count}</TableCell>
                    <TableCell className="text-right tabular-nums">{dollars(guest.total_purchase_amount_cents)}</TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(guest.last_purchase_at)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{guest.source}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {accumulated.length > 0 && (
          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <span>Showing {accumulated.length} of {pagination.count}</span>
            {pagination.next && (
              <Button variant="outline" size="sm" onClick={loadMore}>
                Load more
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  )
}

function mergeGuests(prev: GuestRow[], next: GuestRow[]): GuestRow[] {
  const seen = new Set(prev.map(g => g.id))
  const fresh = next.filter(g => !seen.has(g.id))
  return [...prev, ...fresh]
}

function TagFilterPopover({
  availableTags,
  selected,
  onToggle,
  onClear,
}: {
  availableTags: string[]
  selected: string[]
  onToggle: (tag: string) => void
  onClear: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Filter className="size-4" />
          Tags
          {selected.length > 0 && (
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[11px]">{selected.length}</Badge>
          )}
          <ChevronDown className="size-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <Command>
          <CommandInput placeholder="Search tags…" />
          <CommandList>
            <CommandEmpty>No tags found.</CommandEmpty>
            {availableTags.length > 0 && (
              <CommandGroup>
                {availableTags.map(tag => {
                  const checked = selected.includes(tag)
                  return (
                    <CommandItem
                      key={tag}
                      value={tag}
                      onSelect={() => onToggle(tag)}
                      className="flex items-center gap-2"
                    >
                      <span
                        className={`flex size-4 items-center justify-center rounded-sm border ${
                          checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input'
                        }`}
                        aria-hidden="true"
                      >
                        {checked && <Check className="size-3" />}
                      </span>
                      <span className="flex-1">{tag}</span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
        {selected.length > 0 && (
          <div className="border-t p-1">
            <Button variant="ghost" size="sm" className="w-full" onClick={onClear}>
              Clear filter
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

function EventFilterPopover({
  availableEvents,
  selected,
  onChange,
}: {
  availableEvents: GuestEventOption[]
  selected: string
  onChange: (eventId: string) => void
}) {
  const [open, setOpen] = useState(false)
  const selectedEvent = availableEvents.find(ev => ev.id === selected)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2">
          <CalendarDays className="size-4" />
          <span className="max-w-[160px] truncate">{selectedEvent ? selectedEvent.title : 'Event'}</span>
          <ChevronDown className="size-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <Command>
          <CommandInput placeholder="Search events…" />
          <CommandList>
            <CommandEmpty>No events found.</CommandEmpty>
            {availableEvents.length > 0 && (
              <CommandGroup>
                {availableEvents.map(ev => {
                  const checked = ev.id === selected
                  return (
                    <CommandItem
                      key={ev.id}
                      value={`${ev.title} ${ev.id}`}
                      onSelect={() => {
                        onChange(checked ? '' : ev.id)
                        setOpen(false)
                      }}
                      className="flex items-center gap-2"
                    >
                      <span
                        className={`flex size-4 shrink-0 items-center justify-center rounded-sm border ${
                          checked ? 'border-primary bg-primary text-primary-foreground' : 'border-input'
                        }`}
                        aria-hidden="true"
                      >
                        {checked && <Check className="size-3" />}
                      </span>
                      <span className="flex-1 truncate">{ev.title}</span>
                      {(ev.archived || ev.date) && (
                        <span className="text-xs text-muted-foreground">
                          {ev.archived ? 'Archived' : formatDate(ev.date)}
                        </span>
                      )}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
        {selected && (
          <div className="border-t p-1">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => {
                onChange('')
                setOpen(false)
              }}
            >
              Clear filter
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

type AddGuestForm = {
  guest: {
    first_name: string
    last_name: string
    email: string
    phone: string
    address_line1: string
    address_line2: string
    city: string
    region: string
    postal_code: string
    country: string
    notes: string
    tags: string[]
  }
  offline_donation: { event_id: string; amount_cents: string }
}

function AddGuestDialog({ availableEvents, availableTags }: { availableEvents: GuestEventOption[]; availableTags: string[] }) {
  const [open, setOpen] = useState(false)
  const [logDonation, setLogDonation] = useState(false)

  const { data, setData, post, processing, errors, reset, transform } = useForm<AddGuestForm>({
    guest: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address_line1: '',
      address_line2: '',
      city: '',
      region: '',
      postal_code: '',
      country: '',
      notes: '',
      tags: [],
    },
    offline_donation: { event_id: '', amount_cents: '' },
  })

  transform((d) => {
    if (!logDonation) return { guest: d.guest }
    const dollars = Number(d.offline_donation.amount_cents)
    const cents = Number.isFinite(dollars) ? Math.round(dollars * 100) : 0
    return {
      guest: d.guest,
      offline_donation: { event_id: d.offline_donation.event_id, amount_cents: String(cents) },
    }
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    post('/dashboard/guests', {
      preserveScroll: true,
      onSuccess: () => {
        reset()
        setLogDonation(false)
        setOpen(false)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="size-4" />
          Add guest
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add a guest</DialogTitle>
          <DialogDescription>
            For people you met offline. They&rsquo;ll appear in your guest list right away.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="First name" name="first_name" value={data.guest.first_name}
                   onChange={v => setData('guest', { ...data.guest, first_name: v })}
                   error={errors['guest.first_name']} />
            <Field label="Last name" name="last_name" value={data.guest.last_name}
                   onChange={v => setData('guest', { ...data.guest, last_name: v })}
                   error={errors['guest.last_name']} />
          </div>
          <Field label="Email" name="email" required value={data.guest.email}
                 onChange={v => setData('guest', { ...data.guest, email: v })}
                 error={errors['guest.email']} />
          <Field label="Phone" name="phone" value={data.guest.phone}
                 onChange={v => setData('guest', { ...data.guest, phone: v })}
                 error={errors['guest.phone']} />

          <TagCombobox
            label="Tags"
            placeholder="Search or add a tag…"
            suggestions={availableTags}
            selected={data.guest.tags}
            onChange={tags => setData('guest', { ...data.guest, tags })}
            allowCustom
          />

          <div className="rounded-md border bg-muted/30 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="log-offline-donation"
                checked={logDonation}
                onCheckedChange={(v) => setLogDonation(v === true)}
              />
              <Label htmlFor="log-offline-donation" className="cursor-pointer text-sm font-medium">
                Log offline donation
              </Label>
            </div>
            {logDonation && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="event_id">Event</Label>
                  <Select
                    value={data.offline_donation.event_id}
                    onValueChange={v => setData('offline_donation', { ...data.offline_donation, event_id: v })}
                  >
                    <SelectTrigger id="event_id" className="w-full">
                      <SelectValue placeholder="Select an event" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {availableEvents.map(ev => (
                          <SelectItem key={ev.id} value={ev.id}>
                            {ev.title}{ev.date ? ` · ${formatDate(ev.date)}` : ''}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount_cents">Amount (USD)</Label>
                  <Input
                    id="amount_cents"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    value={data.offline_donation.amount_cents}
                    onChange={e => setData('offline_donation', { ...data.offline_donation, amount_cents: e.target.value })}
                    placeholder="500.00"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={processing}>{processing ? 'Saving…' : 'Add guest'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function Field({
  label,
  name,
  value,
  onChange,
  error,
  required,
}: {
  label: string
  name: string
  value: string
  onChange: (v: string) => void
  error?: string | string[]
  required?: boolean
}) {
  const message = Array.isArray(error) ? error[0] : error
  return (
    <div className="space-y-2">
      <Label htmlFor={name}>{label}{required && <span className="text-destructive"> *</span>}</Label>
      <Input
        id={name}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
      />
      {message && <p className="text-xs text-destructive">{message}</p>}
    </div>
  )
}

DashboardGuestsIndex.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
